import { Suspense } from 'react'
import {
  createBrowserRouter,
  RouterProvider,
} from 'react-router-dom'
import { PublicLayout } from './layout/public-layout'
import HomePage from '@/features/home/home-page'
import NotFoundPage from './routes/not-found'
import { RouteFallback } from './routes/route-fallback'
import { RouteErrorBoundary } from '@/shared/ui/route-error-boundary'
import { ProtectedRoute } from '@/features/auth/components/protected-route'
import { PageStatusGate } from '@/features/page-status/page-status-gate'
import { lazyWithRetry as lazy } from '@/shared/lib/lazy-with-retry'

/**
 * App router. Code-splitting per route, suspense fallback for the loading
 * state, and a per-route `RouteErrorBoundary` so a render bug on one
 * surface doesn't take down the navbar or other routes.
 *
 * `withFallback(Component, routeId)`: every route owns a stable `routeId`
 * the boundary uses for tagging error reports — useful when scanning
 * Sentry to spot a single page failing repeatedly.
 */
const ToolDetailPage = lazy(() => import('@/features/tools-hub/tool-detail-page'), 'ToolDetailPage')
const ToolsHubShellPage = lazy(() => import('@/features/tools-hub/tools-hub-shell-page'), 'ToolsHubShellPage')
const FreeToolsPage = lazy(() => import('@/features/free-tools/free-tools-page'), 'FreeToolsPage')
const FreeToolDetailPage = lazy(() => import('@/features/free-tools/free-tool-detail-page'), 'FreeToolDetailPage')
const ProToolsPage = lazy(() => import('@/features/pro-tools/pro-tools-page'), 'ProToolsPage')
const AiSearchPage = lazy(() => import('@/features/ai-search/ai-search-page'), 'AiSearchPage')
const ZatoonaHubPage = lazy(() => import('@/features/zatoona/zatoona-hub-page'), 'ZatoonaHubPage')
const ZatoonaCategoryPage = lazy(() => import('@/features/zatoona/zatoona-category-page'), 'ZatoonaCategoryPage')
const ZatoonaReferencePage = lazy(() => import('@/features/zatoona/zatoona-reference-page'), 'ZatoonaReferencePage')
const PromptGuidePage = lazy(() => import('@/features/prompt-guide/prompt-guide-page'), 'PromptGuidePage')
const FreeApisHubPage = lazy(() => import('@/features/free-apis/free-apis-hub-page'), 'FreeApisHubPage')
const FreeApiDetailPage = lazy(() => import('@/features/free-apis/free-api-detail-page'), 'FreeApiDetailPage')
const PromptDetailPage = lazy(() => import('@/features/prompts/prompt-detail-page'), 'PromptDetailPage')
const PromptsShellPage = lazy(() => import('@/features/prompts/prompts-shell-page'), 'PromptsShellPage')
const WorkflowsPage = lazy(() => import('@/features/workflows/workflows-page'), 'WorkflowsPage')
const WorkflowDetailPage = lazy(() => import('@/features/workflows/workflow-detail-page'), 'WorkflowDetailPage')
const TutorialsPage = lazy(() => import('@/features/tutorials/tutorials-page'), 'TutorialsPage')
const TutorialsPlaylistPage = lazy(() => import('@/features/tutorials/playlist-page'), 'TutorialsPlaylistPage')
const BlogPage = lazy(() => import('@/features/blog/blog-page'), 'BlogPage')
const ArticlePage = lazy(() => import('@/features/blog/article-page'), 'ArticlePage')
const CoursesPage = lazy(() => import('@/features/courses/courses-page'), 'CoursesPage')
const CourseDetailPage = lazy(() => import('@/features/courses/course-detail-page'), 'CourseDetailPage')
const CourseLearnPage = lazy(() => import('@/features/courses/course-learn-page'), 'CourseLearnPage')
const CheckoutPage = lazy(() => import('@/features/checkout/checkout-page'), 'CheckoutPage')
const PaymentSuccessPage = lazy(() => import('@/features/checkout/payment-success-page'), 'PaymentSuccessPage')
const LoginPage = lazy(() => import('@/features/auth/login-page'), 'LoginPage')
const RegisterPage = lazy(() => import('@/features/auth/register-page'), 'RegisterPage')
const ForgotPasswordPage = lazy(() => import('@/features/auth/forgot-password-page'), 'ForgotPasswordPage')
const StudentDashboardPage = lazy(() => import('@/features/student/dashboard-page'), 'StudentDashboardPage')
const StudentSettingsPage = lazy(() => import('@/features/student/settings-page'), 'StudentSettingsPage')
const StudentCertificatesPage = lazy(() => import('@/features/student/certificates-page'), 'StudentCertificatesPage')
const VerifyCertificatePage = lazy(() => import('@/features/student/verify-certificate-page'), 'VerifyCertificatePage')
const LibraryPage = lazy(() => import('@/features/saved-items/library-page'), 'LibraryPage')
const PublicLibraryPage = lazy(() => import('@/features/saved-items/public-library-page'), 'PublicLibraryPage')
const ContactPage = lazy(() => import('@/features/contact/contact-page'), 'ContactPage')
const AboutPage = lazy(() => import('@/features/about/about-page'), 'AboutPage')
const PrivacyPage = lazy(() => import('@/features/legal/privacy-page'), 'PrivacyPage')
const TermsPage = lazy(() => import('@/features/legal/terms-page'), 'TermsPage')
const ComingSoonPage = lazy(() => import('@/app/routes/coming-soon'), 'ComingSoonPage')
const MaintenancePage = lazy(() => import('@/app/routes/maintenance'), 'MaintenancePage')
const VibeCodingPage = lazy(() => import('@/features/vibe-coding/vibe-coding-page'), 'VibeCodingPage')
const VibeDetailPage = lazy(() => import('@/features/vibe-coding/vibe-detail-page'), 'VibeDetailPage')
const DesignSystemsPage = lazy(() => import('@/features/design-systems/design-systems-page'), 'DesignSystemsPage')
const DesignSystemDetailPage = lazy(() => import('@/features/design-systems/design-system-detail-page'), 'DesignSystemDetailPage')
const HowToUseDesignMdPage = lazy(() => import('@/features/design-systems/how-to-use-page'), 'HowToUseDesignMdPage')
const AiCourseLandingPage = lazy(() => import('@/features/ai-course/ai-course-page'), 'AiCourseLandingPage')
const AiToolsPillarPage = lazy(() => import('@/features/pillars/ai-tools-page'), 'AiToolsPillarPage')
const FreeAiToolsPillarPage = lazy(() => import('@/features/pillars/free-ai-tools-page'), 'FreeAiToolsPillarPage')
const AiPromptsPillarPage = lazy(() => import('@/features/pillars/ai-prompts-page'), 'AiPromptsPillarPage')
const AiDesignCoursePillarPage = lazy(() => import('@/features/pillars/ai-design-course-page'), 'AiDesignCoursePillarPage')
const AiVideoCoursePillarPage = lazy(() => import('@/features/pillars/ai-video-course-page'), 'AiVideoCoursePillarPage')
const PeHubPage = lazy(() => import('@/features/prompt-engineering/pe-hub-page'), 'PeHubPage')
const PePage = lazy(() => import('@/features/prompt-engineering/pe-page'), 'PePage')
const GlossaryPage = lazy(() => import('@/features/glossary/glossary-page'), 'GlossaryPage')
const AiNewsPage = lazy(() => import('@/features/ai-news/ai-news-page'), 'AiNewsPage')
const AiNewsDetailPage = lazy(() => import('@/features/ai-news/ai-news-detail-page'), 'AiNewsDetailPage')
const AssistantPage = lazy(() => import('@/features/assistant/assistant-page'), 'AssistantPage')

// Admin (lazy chunk — only loaded when an admin user navigates to /iamnotsite)
const AdminShell = lazy(() =>
  import('@/features/admin/components/admin-shell').then((m) => ({
    default: m.AdminShell,
  })),
)
const AdminDashboardPage = lazy(
  () => import('@/features/admin/dashboard/dashboard-page'),
  'AdminDashboardPage',
)
const AdminArticleCategoriesPage = lazy(
  () =>
    import('@/features/admin/article-categories/article-categories-page'),
  'AdminArticleCategoriesPage',
)
const AdminPromptCategoriesPage = lazy(
  () => import('@/features/admin/prompt-categories/prompt-categories-page'),
  'AdminPromptCategoriesPage',
)
const AdminCouponsPage = lazy(
  () => import('@/features/admin/coupons/coupons-page'),
  'AdminCouponsPage',
)
const AdminSubscriptionsPage = lazy(
  () => import('@/features/admin/subscriptions/subscriptions-page'),
  'AdminSubscriptionsPage',
)
const AdminAnnouncementsPage = lazy(
  () => import('@/features/admin/announcements/announcements-page'),
  'AdminAnnouncementsPage',
)
const AdminContactMessagesPage = lazy(
  () => import('@/features/admin/contact-messages/contact-messages-page'),
  'AdminContactMessagesPage',
)
const AdminChangePasswordPage = lazy(
  () => import('@/features/admin/security/change-password-page'),
  'AdminChangePasswordPage',
)
const AdminPromptEngineeringPage = lazy(
  () => import('@/features/admin/prompt-engineering/prompt-engineering-page'),
  'AdminPromptEngineeringPage',
)
const AdminErrorsPage = lazy(
  () => import('@/features/admin/errors/errors-page'),
  'AdminErrorsPage',
)
const AdminAnalyticsPage = lazy(
  () => import('@/features/admin/analytics/analytics-page'),
  'AdminAnalyticsPage',
)
const AdminToolsStatsPage = lazy(
  () => import('@/features/admin/tools-stats/tools-stats-page'),
  'AdminToolsStatsPage',
)
const AdminCommentsModerationPage = lazy(
  () => import('@/features/admin/comments/comments-moderation-page'),
  'AdminCommentsModerationPage',
)
const AdminAutoPublishPage = lazy(
  () => import('@/features/admin/auto-publish/auto-publish-page'),
  'AdminAutoPublishPage',
)
const AdminMediaLibraryPage = lazy(
  () => import('@/features/admin/media/media-library-page'),
  'AdminMediaLibraryPage',
)
const AdminSubscribersPage = lazy(
  () => import('@/features/admin/subscribers/subscribers-page'),
  'AdminSubscribersPage',
)
const AdminAiNewsPage = lazy(
  () => import('@/features/admin/ai-news/ai-news-page'),
  'AdminAiNewsPage',
)
const AdminIdeaRadarPage = lazy(
  () => import('@/features/admin/idea-radar/idea-radar-page'),
  'AdminIdeaRadarPage',
)
const AdminSocialPublisherPage = lazy(
  () => import('@/features/admin/social-publisher/social-publisher-page'),
  'AdminSocialPublisherPage',
)
const AdminNavbarPage = lazy(
  () => import('@/features/admin/navbar/navbar-page'),
  'AdminNavbarPage',
)
// AI Course
const AdminAiCourseHub = lazy(
  () => import('@/features/admin/ai-course/hub-page'),
  'AdminAiCourseHub',
)
const AdminAiCourseSettings = lazy(
  () => import('@/features/admin/ai-course/settings-page'),
  'AdminAiCourseSettings',
)
const AdminAiCourseVideos = lazy(
  () => import('@/features/admin/ai-course/videos-page'),
  'AdminAiCourseVideos',
)
const AdminAiCourseImages = lazy(
  () => import('@/features/admin/ai-course/images-page'),
  'AdminAiCourseImages',
)
const AdminAiCoursePlans = lazy(
  () => import('@/features/admin/ai-course/plans-page'),
  'AdminAiCoursePlans',
)
const AdminAiCourseTestimonials = lazy(
  () => import('@/features/admin/ai-course/testimonials-page'),
  'AdminAiCourseTestimonials',
)
const AdminAiCourseFaqs = lazy(
  () => import('@/features/admin/ai-course/faqs-page'),
  'AdminAiCourseFaqs',
)
const AdminPagesManager = lazy(
  () => import('@/features/admin/pages-manager/pages-manager-page'),
  'AdminPagesManager',
)
const AdminHomeSectionsPage = lazy(
  () => import('@/features/admin/home-sections/home-sections-page'),
  'AdminHomeSectionsPage',
)
const AdminCoursesPage = lazy(
  () => import('@/features/admin/courses/courses-page'),
  'AdminCoursesPage',
)
const AdminCourseLessonsPage = lazy(
  () => import('@/features/admin/courses/course-lessons-page'),
  'AdminCourseLessonsPage',
)
const AdminCourseCategoriesPage = lazy(
  () => import('@/features/admin/courses/categories-page'),
  'AdminCourseCategoriesPage',
)
const AdminCourseAnnouncementsPage = lazy(
  () => import('@/features/admin/courses/announcements-page'),
  'AdminCourseAnnouncementsPage',
)
const AdminTutorialsPage = lazy(
  () => import('@/features/admin/tutorials/tutorials-page'),
  'AdminTutorialsPage',
)
const AdminTutorialVideosPage = lazy(
  () => import('@/features/admin/tutorials/tutorial-videos-page'),
  'AdminTutorialVideosPage',
)
const AdminPromptsPage = lazy(
  () => import('@/features/admin/prompts/prompts-page'),
  'AdminPromptsPage',
)
const AdminAIToolsPage = lazy(
  () => import('@/features/admin/ai-tools/ai-tools-page'),
  'AdminAIToolsPage',
)
const AdminFreeToolsPage = lazy(
  () => import('@/features/admin/free-tools/free-tools-page'),
  'AdminFreeToolsPage',
)
const AdminReviewsPage = lazy(
  () => import('@/features/admin/reviews/reviews-page'),
  'AdminReviewsPage',
)
const AdminZatoonaPage = lazy(
  () => import('@/features/admin/zatoona/zatoona-page'),
  'AdminZatoonaPage',
)
const AdminSiteSettingsPage = lazy(
  () => import('@/features/admin/site-settings/site-settings-page'),
  'AdminSiteSettingsPage',
)
const AdminQuotasPage = lazy(() => import('@/features/admin/quotas/quotas-page'), 'AdminQuotasPage')
const AdminPaymentsPage = lazy(
  () => import('@/features/admin/payments/payments-page'),
  'AdminPaymentsPage',
)
const AdminStudentsPage = lazy(
  () => import('@/features/admin/students/students-page'),
  'AdminStudentsPage',
)
// AI Studio
const AdminStudioArticles = lazy(
  () => import('@/features/admin/studio/article-generator-page'),
  'AdminStudioArticles',
)
const AdminStudioPrompts = lazy(
  () => import('@/features/admin/studio/prompt-generator-page'),
  'AdminStudioPrompts',
)
const AdminStudioAITools = lazy(
  () => import('@/features/admin/studio/ai-tools-generator-page'),
  'AdminStudioAITools',
)
const AdminStudioZatoona = lazy(
  () => import('@/features/admin/studio/zatoona-generator-page'),
  'AdminStudioZatoona',
)
const AdminStudioCameraAngles = lazy(
  () => import('@/features/admin/studio/camera-angles-page'),
  'AdminStudioCameraAngles',
)
const AdminArticlesPage = lazy(
  () => import('@/features/admin/articles/articles-page'),
  'AdminArticlesPage',
)
const AdminArticleEditorPage = lazy(
  () => import('@/features/admin/articles/article-editor-page'),
  'AdminArticleEditorPage',
)
const AdminArticleNewPage = lazy(
  () => import('@/features/admin/articles/article-new-page'),
  'AdminArticleNewPage',
)
const StudioPage = lazy(() => import('@/features/studio/studio-page'), 'StudioPage')
const BoardsDashboard = lazy(() => import('@/features/boards/boards-dashboard'), 'BoardsDashboard')
const BoardCanvas = lazy(() => import('@/features/boards/board-canvas'), 'BoardCanvas')

const withFallback = (
  Component: React.ComponentType,
  routeId: string,
) => (
  <RouteErrorBoundary routeId={routeId}>
    <Suspense fallback={<RouteFallback />}>
      <Component />
    </Suspense>
  </RouteErrorBoundary>
)

/**
 * Wrap a route element in the page-status gate so the catalog shells
 * (vibe-coding, prompts, toolshub) honour status overrides too. The
 * PublicLayout already wraps its children, so this is for routes that
 * don't share that layout.
 */
const gated = (element: React.ReactNode) => (
  <PageStatusGate>{element}</PageStatusGate>
)

const router = createBrowserRouter([
  {
    path: '/',
    element: <PublicLayout />,
    children: [
      { index: true, element: <HomePage /> },

      { path: 'courses', element: withFallback(CoursesPage, 'courses') },
      {
        path: 'course/:slug',
        element: withFallback(CourseDetailPage, 'course-detail'),
      },

      { path: 'tutorials', element: withFallback(TutorialsPage, 'tutorials') },
      {
        path: 'tutorials/playlist/:slug',
        element: withFallback(TutorialsPlaylistPage, 'tutorials-playlist'),
      },

      // Pro tools (internal, احترافية) — المبنية جوا الموقع
      { path: 'tools', element: withFallback(ProToolsPage, 'pro-tools') },
      // Free tools hub (مجانية) — hosts the site's OWN internal tools, not
      // external links. The old comment here said "14 tool خارجي"; measured on
      // 2026-09-03 the free_tools table has 19 rows with is_external=0, and
      // every /freetools/* route below resolves to a page in this repo.
      { path: 'freetools', element: withFallback(FreeToolsPage, 'freetools') },
      { path: 'freetools/ai-search', element: withFallback(AiSearchPage, 'ai-search') },
      { path: 'freetools/ai-search/c/:threadId', element: withFallback(AiSearchPage, 'ai-search-thread') },
      { path: 'freetools/ai-search/history', element: withFallback(AiSearchPage, 'ai-search-history') },
      {
        path: 'freetools/:slug',
        element: withFallback(FreeToolDetailPage, 'freetool-detail'),
      },
      // Legacy /tools/:slug internal tool detail lives also under /tools/:slug
      {
        path: 'tools/:slug',
        element: withFallback(FreeToolDetailPage, 'pro-tool-detail'),
      },

      { path: 'zatoona', element: withFallback(ZatoonaHubPage, 'zatoona') },
      {
        path: 'zatoona/:slug',
        element: withFallback(ZatoonaCategoryPage, 'zatoona-category'),
      },
      {
        path: 'zatoona/:categorySlug/:slug',
        element: withFallback(ZatoonaReferencePage, 'zatoona-reference'),
      },

      {
        path: 'prompt-guide',
        element: withFallback(PromptGuidePage, 'prompt-guide'),
      },

      { path: 'free-apis/:slug', element: withFallback(FreeApiDetailPage, 'free-api-detail') },

      { path: 'blog/:slug', element: withFallback(ArticlePage, 'article') },

      {
        path: 'library',
        element: withFallback(PublicLibraryPage, 'library'),
      },

      {
        path: 'glossary',
        element: withFallback(GlossaryPage, 'glossary'),
      },

      { path: 'ai-news', element: withFallback(AiNewsPage, 'ai-news') },
      {
        path: 'ai-news/:slug',
        element: withFallback(AiNewsDetailPage, 'ai-news-detail'),
      },

      {
        path: 'about',
        element: withFallback(AboutPage, 'about'),
      },
      { path: 'contact', element: withFallback(ContactPage, 'contact') },
      { path: 'privacy', element: withFallback(PrivacyPage, 'privacy') },
      { path: 'terms', element: withFallback(TermsPage, 'terms') },
      {
        path: 'coming-soon',
        element: withFallback(ComingSoonPage, 'coming-soon'),
      },
      {
        path: 'maintenance',
        element: withFallback(MaintenancePage, 'maintenance'),
      },

      {
        path: 'vibe-coding/:kind/:slug',
        element: withFallback(VibeDetailPage, 'vibe-detail'),
      },
      {
        path: 'ai-course',
        element: withFallback(AiCourseLandingPage, 'ai-course'),
      },
      { path: 'checkout', element: withFallback(CheckoutPage, 'checkout') },
      {
        path: 'pricing',
        element: withFallback(lazy(() => import('@/features/pricing/pricing-page'), 'PricingPage'), 'pricing'),
      },
      {
        path: 'payment-success',
        element: withFallback(PaymentSuccessPage, 'payment-success'),
      },
      {
        path: 'verify/:certificateNumber',
        element: withFallback(VerifyCertificatePage, 'verify-certificate'),
      },
      { path: 'assistant', element: withFallback(AssistantPage, 'assistant') },

      { path: 'ai-tools', element: withFallback(AiToolsPillarPage, 'pillar-ai-tools') },
      { path: 'free-ai-tools', element: withFallback(FreeAiToolsPillarPage, 'pillar-free-ai-tools') },
      { path: 'ai-prompts', element: withFallback(AiPromptsPillarPage, 'pillar-ai-prompts') },
      { path: 'ai-design-course', element: withFallback(AiDesignCoursePillarPage, 'pillar-ai-design') },
      { path: 'ai-video-course', element: withFallback(AiVideoCoursePillarPage, 'pillar-ai-video') },

      { path: 'studio', element: withFallback(StudioPage, 'studio') },
      { path: 'studio/:projectId', element: withFallback(StudioPage, 'studio-project') },
      { path: 'boards', element: withFallback(BoardsDashboard, 'boards') },
      { path: 'boards/:boardId', element: withFallback(BoardCanvas, 'board-canvas') },
      { path: '*', element: <NotFoundPage /> },
    ],
  },
  // Auth routes — no public layout (focused split-screen surface)
  { path: '/login', element: withFallback(LoginPage, 'login') },
  { path: '/register', element: withFallback(RegisterPage, 'register') },
  {
    path: '/forgot-password',
    element: withFallback(ForgotPasswordPage, 'forgot-password'),
  },

  // LLM Leaderboard removed; blog has its own catalog shell (sidebar + top bar)
  { path: '/blog', element: gated(withFallback(BlogPage, 'blog')) },
  // Vibe Coding catalog — its own shell (sidebar + top bar)
  { path: '/vibe-coding', element: gated(withFallback(VibeCodingPage, 'vibe-coding')) },
  // Design Systems catalog — same shell pattern as vibe-coding
  { path: '/design-systems', element: gated(withFallback(DesignSystemsPage, 'design-systems')) },
  { path: '/design-systems/how-to-use', element: gated(withFallback(HowToUseDesignMdPage, 'design-systems-how-to')) },
  { path: '/design-systems/:slug', element: gated(withFallback(DesignSystemDetailPage, 'design-system-detail')) },
  // Prompts catalog — same shell layout
  { path: '/prompts', element: gated(withFallback(PromptsShellPage, 'prompts')) },
  { path: '/prompts/:id', element: gated(withFallback(PromptDetailPage, 'prompt-detail')) },
  // Workflows library (live n8n templates + Arabic guides) — same shell layout
  { path: '/workflows', element: gated(withFallback(WorkflowsPage, 'workflows')) },
  { path: '/workflows/:id', element: gated(withFallback(WorkflowDetailPage, 'workflow-detail')) },
  // Tools Hub catalog — same shell layout
  { path: '/toolshub', element: gated(withFallback(ToolsHubShellPage, 'toolshub')) },
  { path: '/toolshub/:slug', element: gated(withFallback(ToolDetailPage, 'tool-detail')) },

  // Free APIs Directory — same catalog shell
  { path: '/free-apis', element: gated(withFallback(FreeApisHubPage, 'free-apis')) },

  // Prompt Engineering Guide — bilingual reference, same shell layout
  {
    path: '/prompt-engineering',
    element: gated(withFallback(PeHubPage, 'pe-hub')),
  },
  {
    path: '/prompt-engineering/*',
    element: gated(withFallback(PePage, 'pe-page')),
  },

  // Authenticated student area
  {
    path: '/student',
    element: <ProtectedRoute />,
    children: [
      {
        path: 'dashboard',
        element: withFallback(StudentDashboardPage, 'student-dashboard'),
      },
      {
        path: 'settings',
        element: withFallback(StudentSettingsPage, 'student-settings'),
      },
      {
        path: 'library',
        element: withFallback(LibraryPage, 'student-library'),
      },
      {
        path: 'certificates',
        element: withFallback(StudentCertificatesPage, 'student-certificates'),
      },
    ],
  },
  // Authenticated learning view (separate root because it has its own layout)
  {
    path: '/course/:slug/learn',
    element: <ProtectedRoute />,
    children: [
      { index: true, element: withFallback(CourseLearnPage, 'course-learn') },
    ],
  },

  // Admin panel — gated by role check, lazy-loaded
  {
    path: '/iamnotsite',
    element: <ProtectedRoute roles={['admin']} />,
    children: [
      {
        element: (
          <RouteErrorBoundary routeId="admin-shell">
            <Suspense fallback={<RouteFallback />}>
              <AdminShell />
            </Suspense>
          </RouteErrorBoundary>
        ),
        children: [
          {
            index: true,
            element: withFallback(AdminDashboardPage, 'admin-dashboard'),
          },
          {
            path: 'articles/categories',
            element: withFallback(
              AdminArticleCategoriesPage,
              'admin-article-categories',
            ),
          },
          {
            path: 'prompts/categories',
            element: withFallback(
              AdminPromptCategoriesPage,
              'admin-prompt-categories',
            ),
          },
          {
            path: 'coupons',
            element: withFallback(AdminCouponsPage, 'admin-coupons'),
          },
          {
            path: 'subscriptions',
            element: withFallback(AdminSubscriptionsPage, 'admin-subscriptions'),
          },
          {
            path: 'announcements',
            element: withFallback(
              AdminAnnouncementsPage,
              'admin-announcements',
            ),
          },
          {
            path: 'contact-messages',
            element: withFallback(
              AdminContactMessagesPage,
              'admin-contact-messages',
            ),
          },
          {
            path: 'change-password',
            element: withFallback(
              AdminChangePasswordPage,
              'admin-change-password',
            ),
          },
          {
            path: 'navbar',
            element: withFallback(AdminNavbarPage, 'admin-navbar'),
          },
          // AI Course module
          {
            path: 'ai-course',
            element: withFallback(AdminAiCourseHub, 'admin-ai-course-hub'),
          },
          {
            path: 'ai-course/settings',
            element: withFallback(
              AdminAiCourseSettings,
              'admin-ai-course-settings',
            ),
          },
          {
            path: 'ai-course/videos',
            element: withFallback(AdminAiCourseVideos, 'admin-ai-course-videos'),
          },
          {
            path: 'ai-course/images',
            element: withFallback(AdminAiCourseImages, 'admin-ai-course-images'),
          },
          {
            path: 'ai-course/plans',
            element: withFallback(AdminAiCoursePlans, 'admin-ai-course-plans'),
          },
          {
            path: 'ai-course/testimonials',
            element: withFallback(
              AdminAiCourseTestimonials,
              'admin-ai-course-testimonials',
            ),
          },
          {
            path: 'ai-course/faqs',
            element: withFallback(AdminAiCourseFaqs, 'admin-ai-course-faqs'),
          },
          {
            path: 'pages',
            element: withFallback(AdminPagesManager, 'admin-pages-manager'),
          },
          {
            path: 'home-sections',
            element: withFallback(
              AdminHomeSectionsPage,
              'admin-home-sections',
            ),
          },
          {
            path: 'courses',
            element: withFallback(AdminCoursesPage, 'admin-courses'),
          },
          {
            path: 'courses/:courseId/lessons',
            element: withFallback(
              AdminCourseLessonsPage,
              'admin-course-lessons',
            ),
          },
          {
            path: 'courses/categories',
            element: withFallback(
              AdminCourseCategoriesPage,
              'admin-course-categories',
            ),
          },
          {
            path: 'courses/:courseId/announcements',
            element: withFallback(
              AdminCourseAnnouncementsPage,
              'admin-course-announcements',
            ),
          },
          {
            path: 'tutorials',
            element: withFallback(AdminTutorialsPage, 'admin-tutorials'),
          },
          {
            path: 'prompt-engineering',
            element: withFallback(
              AdminPromptEngineeringPage,
              'admin-prompt-engineering',
            ),
          },
          {
            path: 'errors',
            element: withFallback(AdminErrorsPage, 'admin-errors'),
          },
          {
            path: 'analytics',
            element: withFallback(AdminAnalyticsPage, 'admin-analytics'),
          },
          {
            path: 'tools-stats',
            element: withFallback(AdminToolsStatsPage, 'admin-tools-stats'),
          },
          {
            path: 'comments',
            element: withFallback(
              AdminCommentsModerationPage,
              'admin-comments',
            ),
          },
          {
            path: 'auto-publish',
            element: withFallback(
              AdminAutoPublishPage,
              'admin-auto-publish',
            ),
          },
          {
            path: 'media',
            element: withFallback(AdminMediaLibraryPage, 'admin-media'),
          },
          {
            path: 'subscribers',
            element: withFallback(AdminSubscribersPage, 'admin-subscribers'),
          },
          {
            path: 'ai-news',
            element: withFallback(AdminAiNewsPage, 'admin-ai-news'),
          },
          {
            path: 'idea-radar',
            element: withFallback(AdminIdeaRadarPage, 'admin-idea-radar'),
          },
          {
            path: 'social-publisher',
            element: withFallback(AdminSocialPublisherPage, 'admin-social-publisher'),
          },
          {
            path: 'tutorials/:playlistId/videos',
            element: withFallback(
              AdminTutorialVideosPage,
              'admin-tutorial-videos',
            ),
          },
          {
            path: 'prompts',
            element: withFallback(AdminPromptsPage, 'admin-prompts'),
          },
          {
            path: 'ai-tools',
            element: withFallback(AdminAIToolsPage, 'admin-ai-tools'),
          },
          {
            path: 'free-tools',
            element: withFallback(AdminFreeToolsPage, 'admin-free-tools'),
          },
          {
            path: 'reviews',
            element: withFallback(AdminReviewsPage, 'admin-reviews'),
          },
          {
            path: 'zatoona',
            element: withFallback(AdminZatoonaPage, 'admin-zatoona'),
          },
          {
            path: 'site-settings',
            element: withFallback(
              AdminSiteSettingsPage,
              'admin-site-settings',
            ),
          },
          {
            path: 'quotas',
            element: withFallback(AdminQuotasPage, 'admin-quotas'),
          },
          {
            path: 'payments',
            element: withFallback(AdminPaymentsPage, 'admin-payments'),
          },
          {
            path: 'students',
            element: withFallback(AdminStudentsPage, 'admin-students'),
          },
          // AI Studio
          {
            path: 'studio/articles',
            element: withFallback(
              AdminStudioArticles,
              'admin-studio-articles',
            ),
          },
          {
            path: 'studio/prompts',
            element: withFallback(AdminStudioPrompts, 'admin-studio-prompts'),
          },
          {
            path: 'studio/ai-tools',
            element: withFallback(
              AdminStudioAITools,
              'admin-studio-ai-tools',
            ),
          },
          {
            path: 'studio/zatoona',
            element: withFallback(
              AdminStudioZatoona,
              'admin-studio-zatoona',
            ),
          },
          {
            path: 'studio/camera-angles',
            element: withFallback(
              AdminStudioCameraAngles,
              'admin-studio-camera-angles',
            ),
          },
          {
            path: 'articles',
            element: withFallback(AdminArticlesPage, 'admin-articles'),
          },
          {
            path: 'articles/new',
            element: withFallback(AdminArticleNewPage, 'admin-article-new'),
          },
          {
            path: 'articles/:id/edit',
            element: withFallback(
              AdminArticleEditorPage,
              'admin-article-editor',
            ),
          },
          // Catch-all for unknown admin paths — keeps the admin shell
          // (sidebar + topbar) but renders the bilingual not-found page
          // inside instead of breaking the layout.
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
